// we are using a WeakMap to use the asyncCallback function reference as a key
import type { ComponentType } from 'react';

type Arg<UrlParams> = { urlParams: UrlParams };
type AsyncDependencies<Data, UrlParams> = (arg: Arg<UrlParams>) => Promise<Data>;
let requestWithSuspenseCache = createAsyncDependenciesCache();

function createAsyncDependenciesCache() {
    return new WeakMap<
        AsyncDependencies<unknown, any>,
        { resolved: boolean; err?: unknown; data: unknown }
    >();
}

export function getAsyncDependenciesCache() {
    return requestWithSuspenseCache;
}

export function clearAsyncDependenciesCache(): void {
    requestWithSuspenseCache = createAsyncDependenciesCache();
}

/** this function concurrently loads the asyncDependencies and importFunc (component chunk), the promise will be thrown to suspend the
 * closest React.Suspense. The implementation requires a cache outside
 * the React component lifecycle to support request deduplication and caching  */
export function requestWithSuspense<Data, UrlParams extends Record<string, string>>(
    asyncDependencies: AsyncDependencies<Data, UrlParams>,
    importFunc: () => Promise<Record<string, ComponentType<any>> | ComponentType<any>>,
    arg: Arg<UrlParams>,
    onAsyncDependenciesResolved: (data: Data) => void,
): Data {
    const key = asyncDependencies;
    const value = requestWithSuspenseCache.get(key);
    if (value?.err) {
        throw value.err;
    }

    if (value?.resolved) {
        onAsyncDependenciesResolved(value.data as Data);
        return value.data as Data;
    }

    // We throw a promise for it to be caught by the closest React.Suspense
    throw Promise.all([asyncDependencies({ urlParams: arg.urlParams }), importFunc()])
        .then(([data]) => {
            requestWithSuspenseCache.set(key, { resolved: true, data });
            onAsyncDependenciesResolved(data);
        })
        .catch((err) => {
            requestWithSuspenseCache.set(key, { resolved: false, err, data: undefined });
            throw err;
        });
}
